import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardList, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PageHeader from "@/components/PageHeader";
import SearchBox from "@/components/SearchBox";
import EmptyState from "@/components/EmptyState";
import { useCrudList, crudCreate, crudUpdate, crudDelete } from "@/lib/crud";

const ESTADOS = [
  { value: "excelente", label: "Excelente" }, { value: "bom", label: "Bom" },
  { value: "regular", label: "Regular" }, { value: "ruim", label: "Ruim" },
];
const MONTARIA_OPTS = [
  { value: "excelente", label: "Excelente" }, { value: "boa", label: "Boa" },
  { value: "regular", label: "Regular" }, { value: "ruim", label: "Ruim" }, { value: "nao_montou", label: "Não montou" },
];
const FOCOS = [
  { value: "equilibrio", label: "Equilíbrio" }, { value: "coordenacao", label: "Coordenação" },
  { value: "comunicacao", label: "Comunicação" }, { value: "cognicao", label: "Cognição" },
  { value: "social", label: "Social" }, { value: "postural", label: "Postural" },
  { value: "emocional", label: "Emocional" }, { value: "outro", label: "Outro" },
];

const empty = (praticanteId = "", sessaoId = "") => ({
  praticante_id: praticanteId,
  sessao_id: sessaoId,
  data: new Date().toISOString().slice(0, 10),
  estado_geral: "", montaria: "", acompanhante: "",
  atividade_equestre: "", atividade_realizada: "",
  foco_sessao: "",
  intercorrencias: "",
  participacao: 3, comportamento: 3,
  observacoes: "",
});

export default function Evolucoes({ praticanteIdInitial, sessaoIdInitial, onCreated }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPraticanteId = searchParams.get("praticante_id") || praticanteIdInitial;
  const params = urlPraticanteId ? { praticante_id: urlPraticanteId } : {};
  const { items, loading, setItems } = useCrudList("/evolucoes", params);
  const [praticantes, setPraticantes] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty(urlPraticanteId, sessaoIdInitial));
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Lazy load praticantes for select
  useState(() => {
    import("@/lib/api").then(({ default: api }) => {
      api.get("/praticantes").then(({ data }) => setPraticantes(data)).catch(() => {});
    });
  });

  const openNew = (praticanteId) => {
    setEditing(null);
    setForm(empty(praticanteId || urlPraticanteId, sessaoIdInitial));
    setOpen(true);
  };

  // Auto-open form when URL has ?novo=1
  useEffect(() => {
    if (searchParams.get("novo") === "1") {
      openNew(searchParams.get("praticante_id"));
      const next = new URLSearchParams(searchParams);
      next.delete("novo");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams]);
  const openEdit = (e) => {
    setEditing(e);
    setForm({ ...empty(), ...e, foco_sessao: e.foco_sessao || "" });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.praticante_id) return toast.error("Selecione o praticante");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.sessao_id) delete payload.sessao_id;
      if (editing) {
        const upd = await crudUpdate("/evolucoes", editing.id, payload);
        setItems((prev) => prev.map((x) => x.id === upd.id ? upd : x));
        toast.success("Evolução atualizada");
      } else {
        const c = await crudCreate("/evolucoes", payload);
        setItems((prev) => [c, ...prev]);
        toast.success("Evolução registrada");
        onCreated?.(c);
      }
      setOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await crudDelete("/evolucoes", deleteId);
      setItems((prev) => prev.filter((x) => x.id !== deleteId));
      toast.success("Evolução excluída");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setDeleteId(null); }
  };

  const filtered = q ? items.filter((i) =>
    (i.praticante_nome || "").toLowerCase().includes(q.toLowerCase()) ||
    (i.atividade_realizada || "").toLowerCase().includes(q.toLowerCase())
  ) : items;

  return (
    <div data-testid="evolucoes-page">
      <PageHeader
        icon={ClipboardList}
        title="Evolução Clínica"
        description="Registro das sessões de equoterapia por praticante."
        actions={<Button onClick={openNew} className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white" data-testid="evolucao-new-btn"><Plus className="h-4 w-4 mr-1" />Nova evolução</Button>}
      />

      <div className="mb-4"><SearchBox value={q} onChange={setQ} placeholder="Buscar por praticante ou atividade…" testId="evolucoes-search" /></div>

      <Card className="border-border shadow-none rounded-xl bg-white">
        <CardContent className="p-0">
          {loading ? <div className="p-10 text-center text-muted-foreground">Carregando…</div>
            : filtered.length === 0 ? <div className="p-6"><EmptyState icon={ClipboardList} title="Nenhuma evolução" description="Registre a primeira evolução clínica." action={<Button onClick={openNew} className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white"><Plus className="h-4 w-4 mr-1" />Nova evolução</Button>} /></div>
            : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Data</TableHead><TableHead>Praticante</TableHead>
                  <TableHead>Estado</TableHead><TableHead>Montaria</TableHead>
                  <TableHead>Foco</TableHead><TableHead>Participação</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.map((e) => (
                    <TableRow key={e.id} data-testid={`evolucao-row-${e.id}`}>
                      <TableCell>{e.data}</TableCell>
                      <TableCell className="font-medium">{e.praticante_nome || "—"}</TableCell>
                      <TableCell><Badge variant="secondary">{e.estado_geral || "—"}</Badge></TableCell>
                      <TableCell>{e.montaria || "—"}</TableCell>
                      <TableCell className="max-w-xs truncate">{e.foco_sessao || "—"}</TableCell>
                      <TableCell>{e.participacao != null ? `${e.participacao}/4` : "—"}</TableCell>
                      <TableCell><div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(e)} data-testid={`evolucao-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => setDeleteId(e.id)} data-testid={`evolucao-delete-${e.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar evolução" : "Nova evolução"}</DialogTitle>
            <DialogDescription>Registro clínico da sessão.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <F label="Data" req><Input type="date" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="evolucao-form-data" /></F>
              <F label="Praticante" req className="md:col-span-2">
                <Select value={form.praticante_id} onValueChange={(v) => setForm({ ...form, praticante_id: v })} disabled={!!praticanteIdInitial}>
                  <SelectTrigger data-testid="evolucao-form-praticante"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>{praticantes.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <F label="Estado geral">
                <Select value={form.estado_geral || ""} onValueChange={(v) => setForm({ ...form, estado_geral: v })}>
                  <SelectTrigger data-testid="evolucao-form-estado"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>{ESTADOS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Montaria">
                <Select value={form.montaria || ""} onValueChange={(v) => setForm({ ...form, montaria: v })}>
                  <SelectTrigger data-testid="evolucao-form-montaria"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>{MONTARIA_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Acompanhante"><Input value={form.acompanhante || ""} placeholder="Nome do acompanhante" onChange={(e) => setForm({ ...form, acompanhante: e.target.value })} /></F>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Atividade equestre"><Input value={form.atividade_equestre || ""} placeholder="Ex: passo, trote, exercícios sobre o cavalo" onChange={(e) => setForm({ ...form, atividade_equestre: e.target.value })} /></F>
              <F label="Atividade realizada"><Input value={form.atividade_realizada || ""} placeholder="Ex: circuito com bolas, argolas" onChange={(e) => setForm({ ...form, atividade_realizada: e.target.value })} data-testid="evolucao-form-atividade" /></F>
            </div>

            <F label="Foco da sessão">
              <Textarea
                rows={2}
                value={form.foco_sessao || ""}
                onChange={(e) => setForm({ ...form, foco_sessao: e.target.value })}
                placeholder="Ex: equilíbrio, coordenação, comunicação, tônus muscular…"
                data-testid="evolucao-foco"
              />
            </F>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Scale label="Participação (0-4)" value={form.participacao} onChange={(v) => setForm({ ...form, participacao: v })} testId="evolucao-participacao" />
              <Scale label="Comportamento (0-4)" value={form.comportamento} onChange={(v) => setForm({ ...form, comportamento: v })} testId="evolucao-comportamento" />
            </div>

            <F label="Intercorrências"><Textarea rows={2} value={form.intercorrencias || ""} onChange={(e) => setForm({ ...form, intercorrencias: e.target.value })} placeholder="Descreva qualquer intercorrência (se houver)" data-testid="evolucao-form-intercorrencias" /></F>
            <F label="Observações"><Textarea rows={2} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></F>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white" data-testid="evolucao-form-submit">{saving ? "Salvando…" : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir evolução?</AlertDialogTitle><AlertDialogDescription>Ação irreversível.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid="evolucao-delete-confirm">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Scale({ label, value, onChange, testId }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        {[0, 1, 2, 3, 4].map((v) => (
          <button key={v} type="button" onClick={() => onChange(v)}
            className={`flex-1 h-11 rounded-lg border text-sm font-semibold transition-colors ${value === v ? "bg-brand-lilac text-white border-brand-lilac" : "bg-white border-border hover:bg-secondary/40"}`}
          >{v}</button>
        ))}
      </div>
    </div>
  );
}

function F({ label, req, className, children }) {
  return (<div className={`space-y-1.5 ${className || ""}`}><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}{req && <span className="text-destructive"> *</span>}</Label>{children}</div>);
}
